import { useState, useEffect } from "react";
import { apiClient } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  Fuel, 
  Plus, 
  MoreVertical, 
  Trash2, 
  Search,
  TrendingUp,
  Gauge
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";

const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-TZ", {
    style: "currency",
    currency: "TZS",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

export default function FuelRecordsPage() {
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [vehicleFilter, setVehicleFilter] = useState("all");
  
  const [formData, setFormData] = useState({
    vehicle_id: "",
    liters: 0,
    cost_per_liter: 3000,
    total_cost: 0,
    mileage: 0,
    fuel_date: new Date().toISOString().split("T")[0],
    organization_id: ""
  });

  const canEdit = user?.role !== "driver" || true; // Allow drivers to add fuel records

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [recordsRes, vehiclesRes] = await Promise.all([
        apiClient.get("/fuel-records"),
        apiClient.get("/vehicles")
      ]);
      setRecords(recordsRes.data);
      setVehicles(vehiclesRes.data);
      
      if (vehiclesRes.data.length > 0 && !formData.vehicle_id) {
        setFormData(prev => ({ 
          ...prev, 
          vehicle_id: vehiclesRes.data[0].id,
          organization_id: vehiclesRes.data[0].organization_id,
          mileage: vehiclesRes.data[0].mileage
        }));
      }
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        total_cost: formData.liters * formData.cost_per_liter
      };
      await apiClient.post("/fuel-records", payload);
      toast.success("Fuel record added successfully");
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleDelete = async (recordId) => {
    if (!confirm("Are you sure you want to delete this record?")) return;
    try {
      await apiClient.delete(`/fuel-records/${recordId}`);
      toast.success("Fuel record deleted successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete record");
    }
  };

  const resetForm = () => {
    const defaultVehicle = vehicles[0];
    setFormData({
      vehicle_id: defaultVehicle?.id || "",
      liters: 0,
      cost_per_liter: 3000,
      total_cost: 0,
      mileage: defaultVehicle?.mileage || 0,
      fuel_date: new Date().toISOString().split("T")[0],
      organization_id: defaultVehicle?.organization_id || ""
    });
  };

  const handleVehicleChange = (vehicleId) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    setFormData({ 
      ...formData, 
      vehicle_id: vehicleId,
      organization_id: vehicle?.organization_id || "",
      mileage: vehicle?.mileage || 0
    });
  };

  const updateTotalCost = (liters, costPerLiter) => {
    setFormData({
      ...formData,
      liters,
      cost_per_liter: costPerLiter,
      total_cost: liters * costPerLiter
    });
  };

  const filteredRecords = records.filter(record => {
    const vehicle = vehicles.find(v => v.id === record.vehicle_id);
    const vehicleInfo = vehicle ? `${vehicle.registration_number} ${vehicle.make} ${vehicle.model}` : "";
    const matchesSearch = vehicleInfo.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesVehicle = vehicleFilter === "all" || record.vehicle_id === vehicleFilter;
    return matchesSearch && matchesVehicle;
  });

  const getVehicleInfo = (vehicleId) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    return vehicle ? `${vehicle.registration_number} - ${vehicle.make} ${vehicle.model}` : "Unknown";
  };

  const totalCost = filteredRecords.reduce((sum, r) => sum + r.total_cost, 0);
  const totalLiters = filteredRecords.reduce((sum, r) => sum + r.liters, 0);
  const avgCostPerLiter = totalLiters > 0 ? totalCost / totalLiters : 0;

  if (loading) {
    return (
      <div className="space-y-6" data-testid="fuel-loading">
        <div className="skeleton-tactical h-8 w-48 rounded" />
        <div className="skeleton-tactical h-64 w-full rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="fuel-records-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight text-foreground">
            FUEL RECORDS
          </h1>
          <p className="text-sm font-mono text-muted-foreground mt-1">
            Track fuel consumption and costs
          </p>
        </div>
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-heading tracking-wider" data-testid="add-fuel-btn">
                <Plus className="w-4 h-4 mr-2" />
                ADD RECORD
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border rounded-sm max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading text-lg tracking-wide">
                  ADD FUEL RECORD
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="fuel-form">
                <div className="space-y-2">
                  <Label className="text-xs font-mono text-muted-foreground uppercase">Vehicle</Label>
                  <Select value={formData.vehicle_id} onValueChange={handleVehicleChange}>
                    <SelectTrigger className="bg-input border-border rounded-sm font-mono text-sm" data-testid="fuel-vehicle-select">
                      <SelectValue placeholder="Select vehicle" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border rounded-sm">
                      {vehicles.map(vehicle => (
                        <SelectItem key={vehicle.id} value={vehicle.id} className="font-mono text-sm">
                          {vehicle.registration_number} - {vehicle.make} {vehicle.model}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-mono text-muted-foreground uppercase">Liters</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.liters}
                      onChange={(e) => updateTotalCost(parseFloat(e.target.value), formData.cost_per_liter)}
                      className="bg-input border-border rounded-sm font-mono text-sm"
                      required
                      data-testid="fuel-liters-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-mono text-muted-foreground uppercase">Cost/Liter (TZS)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.cost_per_liter}
                      onChange={(e) => updateTotalCost(formData.liters, parseFloat(e.target.value))}
                      className="bg-input border-border rounded-sm font-mono text-sm"
                      required
                      data-testid="fuel-cost-input"
                    />
                  </div>
                </div>

                <div className="p-3 bg-accent/50 rounded-sm border border-border">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-mono text-muted-foreground">TOTAL COST</span>
                    <span className="text-lg font-heading font-bold text-primary">
                      {formatCurrency(formData.liters * formData.cost_per_liter)}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-mono text-muted-foreground uppercase">Mileage (km)</Label>
                    <Input
                      type="number"
                      value={formData.mileage}
                      onChange={(e) => setFormData({ ...formData, mileage: parseInt(e.target.value) })}
                      className="bg-input border-border rounded-sm font-mono text-sm"
                      required
                      data-testid="fuel-mileage-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-mono text-muted-foreground uppercase">Date</Label>
                    <Input
                      type="date"
                      value={formData.fuel_date}
                      onChange={(e) => setFormData({ ...formData, fuel_date: e.target.value })}
                      className="bg-input border-border rounded-sm font-mono text-sm"
                      required
                      data-testid="fuel-date-input"
                    />
                  </div>
                </div>

                <Button type="submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-heading tracking-wider" data-testid="fuel-submit-btn">
                  ADD RECORD
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-sm bg-primary/10 border border-primary/30 flex items-center justify-center">
              <Fuel className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-heading font-bold text-foreground">{totalLiters.toFixed(1)} L</p>
              <p className="text-xs font-mono text-muted-foreground">TOTAL LITERS</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-sm bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-yellow-400" />
            </div>
            <div>
              <p className="text-xl font-heading font-bold text-foreground">{formatCurrency(totalCost)}</p>
              <p className="text-xs font-mono text-muted-foreground">TOTAL COST</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-sm bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
              <Gauge className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-xl font-heading font-bold text-foreground">{formatCurrency(avgCostPerLiter)}/L</p>
              <p className="text-xs font-mono text-muted-foreground">AVG COST/LITER</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-card border-border rounded-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search records..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-input border-border rounded-sm font-mono text-sm"
                data-testid="fuel-search-input"
              />
            </div>
            <Select value={vehicleFilter} onValueChange={setVehicleFilter}>
              <SelectTrigger className="w-full sm:w-48 bg-input border-border rounded-sm font-mono text-sm" data-testid="fuel-vehicle-filter">
                <SelectValue placeholder="Filter by vehicle" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border rounded-sm">
                <SelectItem value="all" className="font-mono text-sm">All Vehicles</SelectItem>
                {vehicles.map(vehicle => (
                  <SelectItem key={vehicle.id} value={vehicle.id} className="font-mono text-sm">
                    {vehicle.registration_number}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Records Table */}
      <Card className="bg-card border-border rounded-sm" data-testid="fuel-table">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">DATE</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">VEHICLE</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">LITERS</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">COST/LITER</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">TOTAL COST</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">MILEAGE</TableHead>
                  {canEdit && <TableHead className="font-heading text-xs tracking-wide text-muted-foreground text-right">ACTIONS</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRecords.map((record) => (
                  <TableRow key={record.id} className="border-border hover:bg-accent/50" data-testid={`fuel-row-${record.id}`}>
                    <TableCell className="font-mono text-sm text-muted-foreground">
                      {record.fuel_date}
                    </TableCell>
                    <TableCell className="font-mono text-sm text-primary">
                      {getVehicleInfo(record.vehicle_id)}
                    </TableCell>
                    <TableCell className="font-mono text-sm text-foreground">
                      {record.liters.toFixed(2)} L
                    </TableCell>
                    <TableCell className="font-mono text-sm text-muted-foreground">
                      {formatCurrency(record.cost_per_liter)}
                    </TableCell>
                    <TableCell className="font-mono text-sm text-yellow-400 font-medium">
                      {formatCurrency(record.total_cost)}
                    </TableCell>
                    <TableCell className="font-mono text-sm text-muted-foreground">
                      {record.mileage.toLocaleString()} km
                    </TableCell>
                    {canEdit && (
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`fuel-actions-${record.id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-card border-border rounded-sm">
                            <DropdownMenuItem 
                              onClick={() => handleDelete(record.id)} 
                              className="cursor-pointer text-destructive focus:text-destructive"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {filteredRecords.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={canEdit ? 7 : 6} className="text-center py-8 text-muted-foreground font-mono">
                      No fuel records found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
